<template>
  <div>
    <div v-if="loading" class="text-center py-4">
      <CSpinner color="primary" />
      <p class="mt-2">Загрузка...</p>
    </div>
    
    <div v-else-if="test">
    <CCard>
      <CCardHeader>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <strong>{{ test.name }}</strong>
            <CBadge :color="test.active ? 'success' : 'secondary'" class="ml-2">
              {{ test.active ? 'Активен' : 'Неактивен' }}
            </CBadge>
          </div>
          <div>
            <CButton
              color="info"
              variant="outline"
              size="sm"
              class="mr-2"
              @click="$router.push(`/ab-tests/${test.id}/edit`)"
            >
              <CIcon name="cil-pencil" class="mr-1" />
              Редактировать
            </CButton>
            <CButton
              color="success"
              variant="outline"
              size="sm"
              @click="$router.push(`/ab-tests/${test.id}/statistics`)"
            >
              <CIcon name="cil-chart-line" class="mr-1" />
              Статистика
            </CButton>
          </div>
        </div>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol md="6">
            <table class="table table-borderless">
              <tbody>
                <tr>
                  <td class="font-weight-bold">ID:</td>
                  <td>{{ test.id }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Ключ:</td>
                  <td><code>{{ test.key }}</code></td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Трафик:</td>
                  <td>{{ test.traffic_allocation }}%</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Создан:</td>
                  <td>{{ formatDate(test.created_at) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Обновлен:</td>
                  <td>{{ formatDate(test.updated_at) }}</td>
                </tr>
              </tbody>
            </table>
          </CCol>
          <CCol md="6">
            <div v-if="test.description">
              <h6>Описание:</h6>
              <p>{{ test.description }}</p>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardHeader>
        <div class="d-flex justify-content-between align-items-center">
          <strong>Варианты теста</strong>
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            @click="showAddVariantModal = true"
          >
            <CIcon name="cil-plus" class="mr-1" />
            Добавить вариант
          </CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <div v-if="test.variants.length === 0" class="text-center text-muted py-4">
          <p>Варианты не добавлены</p>
        </div>
        <CRow v-else>
          <CCol md="6" v-for="variant in test.variants" :key="variant.id" class="mb-3">
            <CCard>
              <CCardBody>
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h6>{{ variant.name }}</h6>
                    <p class="text-muted mb-1">
                      <strong>Ключ:</strong> <code>{{ variant.key }}</code>
                    </p>
                    <p class="text-muted mb-1">
                      <strong>Вес:</strong> {{ variant.weight }}%
                    </p>
                    <div v-if="variant.config && Object.keys(variant.config).length > 0">
                      <strong>Конфигурация:</strong>
                      <pre class="bg-dark p-2 small mt-1">{{ JSON.stringify(variant.config, null, 2) }}</pre>
                    </div>
                  </div>
                  <div class="d-flex flex-column">
                    <CButton
                      color="info"
                      variant="outline"
                      size="sm"
                      class="mb-1"
                      @click="editVariant(variant)"
                    >
                      ✏️ Редактировать
                    </CButton>
                    <CButton
                      color="danger"
                      variant="outline"
                      size="sm"
                      @click="deleteVariant(variant)"
                    >
                      🗑️ Удалить
                    </CButton>
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
        
        <div v-if="test.variants.length > 0" class="alert alert-info mt-3">
          <strong>Общий вес всех вариантов:</strong> {{ totalWeight }}%
          <span v-if="totalWeight !== 100" class="text-warning ml-2">
            (рекомендуется 100% для корректного распределения)
          </span>
        </div>
      </CCardBody>
    </CCard>

    <!-- Модал редактирования варианта -->
    <CModal
      :show="showEditVariantModal"
      @close="closeEditVariantModal"
      title="Редактировать вариант"
      size="lg"
    >
      <CForm @submit.prevent="updateVariantFromModal">
        <CRow>
          <CCol md="6">
            <CInput
              label="Название"
              v-model="editingVariant.name"
              required
            />
          </CCol>
          <CCol md="6">
            <CInput
              label="Ключ"
              v-model="editingVariant.key"
              required
            />
          </CCol>
        </CRow>
        <CRow>
          <CCol md="6">
            <CInput
              label="Вес (%)"
              v-model.number="editingVariant.weight"
              type="number"
              min="1"
              max="100"
              required
            />
          </CCol>
        </CRow>
        <CRow>
          <CCol md="12">
            <CTextarea
              label="Конфигурация (JSON)"
              v-model="editingVariant.configString"
              rows="4"
              placeholder='{"key": "value"}'
            />
          </CCol>
        </CRow>
      </CForm>
      
      <template #footer>
        <CButton
          color="secondary"
          @click="closeEditVariantModal"
        >
          Отмена
        </CButton>
        <CButton
          color="primary"
          @click="updateVariantFromModal"
          :disabled="!editingVariant.name || !editingVariant.key"
        >
          Сохранить
        </CButton>
      </template>
    </CModal>

    <!-- Модал добавления варианта -->
    <CModal
      :show="showAddVariantModal"
      @close="showAddVariantModal = false"
      title="Добавить вариант"
      size="lg"
    >
      <CForm @submit.prevent="addVariant">
        <CRow>
          <CCol md="6">
            <CInput
              label="Название"
              v-model="newVariant.name"
              required
            />
          </CCol>
          <CCol md="6">
            <CInput
              label="Ключ"
              v-model="newVariant.key"
              required
            />
          </CCol>
        </CRow>
        <CRow>
          <CCol md="6">
            <CInput
              label="Вес (%)"
              v-model.number="newVariant.weight"
              type="number"
              min="1"
              max="100"
              required
            />
          </CCol>
        </CRow>
        <CRow>
          <CCol md="12">
            <CTextarea
              label="Конфигурация (JSON)"
              v-model="newVariant.configString"
              rows="4"
              placeholder='{"key": "value"}'
            />
          </CCol>
        </CRow>
      </CForm>
      
      <template #footer>
        <CButton
          color="secondary"
          @click="showAddVariantModal = false"
        >
          Отмена
        </CButton>
        <CButton
          color="primary"
          @click="addVariant"
          :disabled="!newVariant.name || !newVariant.key"
        >
          Добавить
        </CButton>
      </template>
    </CModal>
    </div>
    
    <div v-else-if="!loading" class="text-center py-4">
      <p class="text-muted">Тест не найден</p>
      <CButton color="primary" @click="$router.push({ path: '/ab-tests' })">
        Вернуться к списку
      </CButton>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'AbTestDetail',
  data() {
    return {
      test: null,
      loading: false,
      showAddVariantModal: false,
      showEditVariantModal: false,
      newVariant: {
        name: '',
        key: '',
        weight: 50,
        configString: ''
      },
      editingVariant: {
        id: null,
        name: '',
        key: '',
        weight: 50,
        configString: ''
      }
    }
  },
  computed: {
    totalWeight() {
      if (!this.test || !this.test.variants) return 0
      return this.test.variants.reduce((sum, variant) => sum + variant.weight, 0)
    }
  },
  async mounted() {
    await this.fetchTest()
  },
  watch: {
    '$route'() {
      if (this.$route.params.id) {
        this.fetchTest()
      }
    }
  },
  methods: {
    async fetchTest() {
      this.loading = true
      try {
        const { data } = await this.$http.get(`/ab-tests/${this.$route.params.id}`)
        this.test = data
      } catch (error) {
        this.$root.$emit('showError', 'Ошибка загрузки теста')
        console.error(error)
        this.$router.push({
          path: '/ab-tests'
        })
      }
      this.loading = false
    },
    
    async addVariant() {
      try {
        // Парсим JSON если указан
        let config = {}
        if (this.newVariant.configString.trim()) {
          try {
            config = JSON.parse(this.newVariant.configString)
          } catch (e) {
            this.$root.$emit('showError', 'Некорректный JSON в конфигурации')
            return
          }
        }
        
        const variantData = {
          name: this.newVariant.name,
          key: this.newVariant.key,
          weight: this.newVariant.weight,
          config
        }
        
                  await this.$http.post(`/ab-tests/${this.test.id}/variants`, variantData)
        this.$root.$emit('showSuccess', 'Вариант успешно добавлен')
        this.showAddVariantModal = false
        this.newVariant = { name: '', key: '', weight: 50, configString: '' }
        await this.fetchTest()
      } catch (error) {
        this.$root.$emit('showError', 'Ошибка добавления варианта')
        console.error(error)
      }
    },
    
    editVariant(variant) {
      // Заполняем форму редактирования данными варианта
      this.editingVariant = {
        id: variant.id,
        name: variant.name,
        key: variant.key,
        weight: variant.weight,
        configString: variant.config ? JSON.stringify(variant.config, null, 2) : ''
      }
      this.showEditVariantModal = true
    },
    
    async updateVariant(variantId, data) {
      try {
        await this.$http.put(`/ab-tests/${this.test.id}/variants/${variantId}`, data)
        this.$root.$emit('showSuccess', 'Вариант обновлен')
        await this.fetchTest()
      } catch (error) {
        this.$root.$emit('showError', 'Ошибка обновления варианта')
        console.error(error)
      }
    },

    async updateVariantFromModal() {
      try {
        // Парсим JSON если указан
        let config = {}
        if (this.editingVariant.configString.trim()) {
          try {
            config = JSON.parse(this.editingVariant.configString)
          } catch (e) {
            this.$root.$emit('showError', 'Некорректный JSON в конфигурации')
            return
          }
        }
        
        const variantData = {
          name: this.editingVariant.name,
          key: this.editingVariant.key,
          weight: this.editingVariant.weight,
          config
        }
        
        await this.$http.put(`/ab-tests/${this.test.id}/variants/${this.editingVariant.id}`, variantData)
        this.$root.$emit('showSuccess', 'Вариант успешно обновлен')
        this.closeEditVariantModal()
        await this.fetchTest()
      } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
          this.$root.$emit('showError', error.response.data.message)
        } else {
          this.$root.$emit('showError', 'Ошибка обновления варианта')
        }
        console.error(error)
      }
    },

    closeEditVariantModal() {
      this.showEditVariantModal = false
      this.editingVariant = {
        id: null,
        name: '',
        key: '',
        weight: 50,
        configString: ''
      }
    },
    
    async deleteVariant(variant) {
      const confirmed = confirm(`Вы уверены, что хотите удалить вариант "${variant.name}"?\n\nЭто действие нельзя отменить.`)
      
      if (confirmed) {
        try {
          await this.$http.delete(`/ab-tests/${this.test.id}/variants/${variant.id}`)
          this.$root.$emit('showSuccess', 'Вариант успешно удален')
          await this.fetchTest()
        } catch (error) {
          if (error.response && error.response.data && error.response.data.message) {
            this.$root.$emit('showError', error.response.data.message)
          } else {
            this.$root.$emit('showError', 'Ошибка удаления варианта')
          }
          console.error(error)
        }
      }
    },
    
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm')
    }
  }
}
</script> 